"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  title: "ATEX & IECEx Compliance",
  description: "Certified hardware for Zone 0, Zone 1, and Zone 2 operating environments."
}, {
  title: "Expert Qualification Support",
  description: "Full IQ/OQ validation protocols and chemometric model development."
}, {
  title: "Ireland & UK Deployment",
  description: "Local technical engineers and fast-response lifecycle maintenance."
}];
export default function HazardousCta() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="hazardousareasolutionscta" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28">
      {/* Background visual accents */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-30">
        <div className="h-[420px] w-[640px] rounded-full bg-primary/10 blur-3xl" />
        <div className="h-[320px] w-[480px] rounded-full bg-chart-2/5 blur-2xl" />
      </div>

      <div className="page-container">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="relative mx-auto max-w-4xl rounded-2xl border border-border bg-card p-8 shadow-lg sm:p-12 lg:p-16">
          {/* Badge */}
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <ShieldCheck className="h-3.5 w-3.5" />
              ATEX / Ex Certified Process Technology
            </span>
          </div>

          {/* Heading and description */}
          <div className="mt-6 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Ready to Deploy Hazardous-Area Solutions?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
              Get in touch with our analytical specialists to assess your process environment, review Zone classification requirements, and configure the right PAT instrumentation.
            </p>
          </div>

          {/* Action buttons */}
          <div className="mt-8 flex flex-col items-center justify-center gap-3.5 sm:flex-row">
            <Button asChild size="lg" className="h-12 w-full px-7 text-base font-semibold sm:w-auto">
              <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                Discuss Your Application
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="h-12 w-full px-7 text-base font-medium sm:w-auto">
              <Link to="/BookADemoPage">
                Book a Demo
              </Link>
            </Button>
          </div>

          {/* Trust points list */}
          <div className="mt-12 border-t border-border/80 pt-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {features.map((item, i) => <div key={item.title} data-index={i} className="flex flex-col items-center text-center sm:items-start sm:text-left">
                  <div className="flex items-center gap-2 font-medium text-foreground">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-sm font-semibold">{item.title}</span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}